import { Chip, DropdownMenu, Icon, SvgIconNames } from '@toggle/design-system';
import React, { RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '~/components/filter/Option';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useReasonerParams } from '~/views/alfred/research/hooks/useReasonerParams';
import { SubmittedAnalysisMode } from '~/views/alfred/research/services/conversations-service';
import { Conversation } from '~/views/alfred/research/use-chat/use-chat.types';
import { useChat } from '~/views/alfred/research/use-chat/useChat';

import styles from './AnalysisModeDropdown.module.scss';

const getConversationAnalysisMode = (
  conversation: Conversation
): SubmittedAnalysisMode | undefined => {
  const lastTask = conversation.tasks?.at(-1);
  const taskMode = lastTask?.submittedAnalysisMode ?? lastTask?.analysis_mode;

  if (taskMode) {
    return taskMode === 'Deep' ? 'Deep' : 'Auto';
  }

  if (conversation.scheduled_query_id) {
    return 'Deep';
  }

  return lastTask ? 'Auto' : undefined;
};

interface DropdownOption {
  key: string;
  icon: SvgIconNames;
  name: string;
  value: {
    analysis_mode: SubmittedAnalysisMode;
  };
  description: string;
}

export interface AnalysisModeDropdownProps {
  isMessagePending: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
}

export const AnalysisModeDropdown = ({
  isMessagePending,
  contentRef,
}: AnalysisModeDropdownProps) => {
  const { t } = useTranslation(['chat']);

  const { conversationId } = useReasonerParams();

  const {
    analysisMode,
    modeForLanding,
    activeConversation,
    setAnalysisMode,
    setModeForLanding,
  } = useChat(state => ({
    analysisMode: state.analysisMode,
    modeForLanding: state.modeForLanding,
    activeConversation: state.activeConversation,
    setAnalysisMode: state.setAnalysisMode,
    setModeForLanding: state.setModeForLanding,
  }));
  const { trackAlfredDeepSelected } = usePosthogEvents();

  const renderCustomFilterOptionLabel = (filterOption: FilterOption) => {
    return (
      <div className={styles.optionWrapper}>
        {filterOption.icon && (
          <Icon
            iconName={filterOption.icon}
            size={16}
            fillColor="var(--text-default)"
          />
        )}
        <span className={styles.label}>{filterOption.name}</span>
      </div>
    );
  };

  const options: DropdownOption[] = [
    {
      key: 'auto',
      icon: 'LightningBolt',
      name: t('chat:analysisMode.autoTitle'),
      value: {
        analysis_mode: 'Auto',
      },
      description: t('chat:analysisMode.autoDescription'),
    },
    {
      key: 'deep',
      icon: 'FileSearch',
      name: t('chat:analysisMode.analystTitle'),
      value: {
        analysis_mode: 'Deep',
      },
      description: t('chat:analysisMode.analystDescription'),
    },
  ];

  const selectedOption = (() => {
    let option = options.find(i => i.value.analysis_mode === analysisMode);

    const lastTask = activeConversation?.tasks?.at(-1);

    if (lastTask?.submittedAnalysisMode && isMessagePending) {
      option = options.find(
        i => i.value.analysis_mode === lastTask.submittedAnalysisMode
      );
    }

    return option;
  })();

  useEffect(() => {
    if (!conversationId) {
      setAnalysisMode(modeForLanding);
    }

    if (activeConversation) {
      const conversationMode = getConversationAnalysisMode(activeConversation);

      if (conversationMode) {
        setAnalysisMode(conversationMode);
      }
    }
  }, [conversationId, activeConversation]);

  return (
    <DropdownMenu
      className={styles.styledDropdownMenu}
      contentRef={contentRef}
      triggerProps={{
        disabled: isMessagePending,
      }}
      triggerNode={
        <div>
          <Chip
            className={styles.styledChip}
            label={selectedOption?.name}
            leftIcon={selectedOption?.icon}
            rightIcon="ChevronLightDown"
            rightIconSize={8}
            disabled={isMessagePending}
            data-testid="analysis-mode-dropdown-trigger"
          />
        </div>
      }
    >
      {options.map(i => (
        <Option
          key={i.key}
          option={i}
          isChecked={analysisMode === i.value.analysis_mode}
          indentedDescription={24}
          handleClick={() => {
            setAnalysisMode(i.value.analysis_mode);

            if (!conversationId) {
              setModeForLanding(i.value.analysis_mode);
            }

            if (i.value.analysis_mode === 'Deep') {
              trackAlfredDeepSelected(POSTHOG_TRACKING_SOURCES.ALFRED);
            }
          }}
          renderCustomFilterOptionLabel={() => renderCustomFilterOptionLabel(i)}
        />
      ))}
    </DropdownMenu>
  );
};
